import {
  WeeklyCheckinEmailReportDataByTimeAndType,
  WeeklyCheckInActivityEmailReportTableData,
} from "../types";
import { responseStatus } from "utils/types";
import { getS } from "utils/helperFunctions";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  WeeklyCheckInInitialTimeInterval,
  WeeklyCheckInTimeIntervals,
} from "app/containers/AdminConsole/constants";
import {
  getDateCell,
  getDropdownFromRecord,
  getSearchInput,
  getViewWeeklyCheckInReportButton,
} from "../helpers";
import {
  CHECK_IN_EMAIL_REPORT_TYPES,
  WEEKLY_CHECK_IN_FEATURE_NAME,
} from "../constants";
import ExportCSVButton from "app/storybookComponents/Button/ExportCSVButton";
import {
  selectAllCompanyUsersById,
  selectCurrentUserAccountId,
  selectIsCurrentUserAdmin,
  selectSampleUsersInfoById,
} from "app/containers/Global/slice";
import { useAppSelector } from "utils/redux/hooks";
import SortableTable from "app/components/SortableTable";
import CheckInEmailReportModal from "../Modals/CheckInEmailReportModal";
import EmptyCard from "app/storybookComponents/Cards/EmptyCard";
import Loading from "app/storybookComponents/Loading";
import SimplePopover from "app/components/Popovers/SimplePopover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { trackCSVExport } from "utils/trackingFunctions";
import {
  selectSampleWeeklyCheckInActivityData,
  selectWeeklyCheckInCompanySettings,
} from "../slice";

interface Props {
  getWeeklyCheckInActivityEmailReportsTableDataStatus: responseStatus;
  weeklyCheckinEmailReportDataByTimeAndType?: WeeklyCheckinEmailReportDataByTimeAndType;
  fetchTableData?: (params: {
    reportType: string;
    timeInterval: string;
  }) => void;
  isSample?: boolean;
}

const CheckInActivityEmailReportsTable = ({
  getWeeklyCheckInActivityEmailReportsTableDataStatus,
  weeklyCheckinEmailReportDataByTimeAndType,
  fetchTableData,
  isSample,
}: Readonly<Props>) => {
  const sampleWeeklyCheckInActivityData = useAppSelector(
    selectSampleWeeklyCheckInActivityData
  );
  const companyUsers = useAppSelector(selectAllCompanyUsersById);
  const sampleUsersById = useAppSelector(selectSampleUsersInfoById);
  const isUserAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const currentUserAccountId = useAppSelector(selectCurrentUserAccountId);
  const [reportType, setReportType] = useState("all");
  const [timeInterval, setTimeInterval] = useState(
    WeeklyCheckInInitialTimeInterval
  );
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );
  const [displayedInput, setDisplayedInput] = useState<string>("");
  const [emailReportModalState, setEmailReportModalState] =
    useState<WeeklyCheckInActivityEmailReportTableData | null>(null);

  const usersById = useMemo(() => {
    if (isSample) {
      return sampleUsersById;
    }
    return companyUsers;
  }, [isSample, sampleUsersById, companyUsers]);

  useEffect(() => {
    if (fetchTableData) {
      fetchTableData({
        reportType,
        timeInterval,
      });
    }
  }, [fetchTableData, timeInterval, reportType]);

  const tableData = useMemo(() => {
    if (isSample) {
      return sampleWeeklyCheckInActivityData?.emailReportTableData ?? [];
    }

    return Object.values(
      weeklyCheckinEmailReportDataByTimeAndType?.[timeInterval]?.[reportType] ??
        {}
    );
  }, [
    weeklyCheckinEmailReportDataByTimeAndType,
    reportType,
    timeInterval,
    isSample,
    sampleWeeklyCheckInActivityData,
  ]);

  const getFilteredRows =
    useCallback((): WeeklyCheckInActivityEmailReportTableData[] => {
      // If there is no input, return all rows
      if (!displayedInput) {
        return tableData ?? [];
      }

      // Otherwise, filter the weeklyCheckInTableDataShowing by user name or email
      return (
        tableData?.filter((row) => {
          const user = usersById[row.userAccountId];
          if (!user) {
            return false;
          }
          const { firstName = "", lastName = "", emailAddress } = user;
          const fullName = `${firstName} ${lastName}`.trim();
          return (
            fullName.toLowerCase().includes(displayedInput.toLowerCase()) ||
            emailAddress.toLowerCase().includes(displayedInput.toLowerCase())
          );
        }) ?? []
      );
    }, [usersById, displayedInput, tableData]);

  const getCSVExportRows = useCallback(() => {
    trackCSVExport("Check In Activity Email Reports Table");
    return getFilteredRows().map((data) => {
      const reportTypes = data.reports.map(
        (report) =>
          CHECK_IN_EMAIL_REPORT_TYPES[report.reportType] ?? report.reportType
      );
      const reportType = reportTypes.join(", ");
      let userName = "";
      const user = usersById[data.userAccountId];
      if (user) {
        const { firstName = "", lastName = "" } = user;
        userName = `${firstName} ${lastName}`.trim();
      }
      return {
        user: userName,
        date: data.dateSent,
        reportType,
      };
    });
  }, [getFilteredRows, usersById]);

  const getDropdownSection = () => {
    if (isSample) {
      return null;
    }
    const isLoading =
      getWeeklyCheckInActivityEmailReportsTableDataStatus === "loading";
    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="row-gap-12px">
          {getDropdownFromRecord(
            WeeklyCheckInTimeIntervals,
            timeInterval,
            (e) => {
              setTimeInterval(e ?? "allTime");
              setDisplayedInput("");
            },
            isLoading
          )}
          {getDropdownFromRecord(
            CHECK_IN_EMAIL_REPORT_TYPES,
            reportType,
            (e) => {
              setReportType(e ?? "all");
              setDisplayedInput("");
            },
            isLoading
          )}
        </div>
        <div>
          <ExportCSVButton
            headers={headers.filter((header) => header.key !== "dropdown")}
            getRows={getCSVExportRows}
            disabled={
              getWeeklyCheckInActivityEmailReportsTableDataStatus === "loading"
            }
          />
        </div>
      </div>
    );
  };

  const getUserCell = (rowData: WeeklyCheckInActivityEmailReportTableData) => {
    const { userAccountId } = rowData;
    const user = usersById[userAccountId];
    if (!user) {
      return "";
    }
    const { firstName = "", lastName = "", jobTitle = "No job title" } = user;
    const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
    return {
      sortValue: fullName,
      displayValue: (
        <div>
          <button
            className="button-link"
            onClick={() => setEmailReportModalState(rowData)}
          >
            <p className="user-name-cell__name">{fullName}</p>
          </button>
          <p className="small-grey-text">{jobTitle}</p>
        </div>
      ),
    };
  };

  const getReportTypeCell = (
    tableRowData: WeeklyCheckInActivityEmailReportTableData
  ) => {
    const { reports } = tableRowData;
    const reportTypeSet = new Set();
    reports.forEach((report) => {
      if (CHECK_IN_EMAIL_REPORT_TYPES[report.reportType]) {
        reportTypeSet.add(CHECK_IN_EMAIL_REPORT_TYPES[report.reportType]);
      }
    });
    const reportTypeString = Array.from(reportTypeSet).join(", ");
    const totalReportLength = reports.length;
    return {
      sortValue: reportTypeString,
      displayValue: (
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <p>{reportTypeString}</p>
            <p className="small-grey-text">
              {totalReportLength} report{getS(totalReportLength)}
            </p>
          </div>
          {getViewWeeklyCheckInReportButton(
            () => setEmailReportModalState(tableRowData),
            weeklyCheckInCompanySettings?.reportVisibilitySettings,
            !!isUserAdmin,
            tableRowData.userAccountId === currentUserAccountId
          )}
        </div>
      ),
    };
  };

  const getTableRow = (row: WeeklyCheckInActivityEmailReportTableData) => {
    const reportIds = row.reports.map((report) => report.checkInEmailReportId);
    const emailReportConcat = [row.userAccountId, ...reportIds].join("_");
    return {
      key: emailReportConcat,
      user: getUserCell(row),
      date: getDateCell(row.dateSent),
      reportType: getReportTypeCell(row),
    };
  };

  const getEmptyState = () => {
    if (getWeeklyCheckInActivityEmailReportsTableDataStatus === "loading") {
      return (
        <div>
          <Loading />
        </div>
      );
    }

    if (rows.length !== 0) {
      return null;
    }

    if (displayedInput) {
      return (
        <EmptyCard
          title={`No users found matching "${displayedInput}"`}
          bodyText="Try searching for a different name."
        />
      );
    }

    return (
      <EmptyCard
        title="No check-in email reports found"
        bodyText="Once there is check-in email reports, they will be shown here."
      />
    );
  };

  const getReportTypePopover = () => (
    <SimplePopover
      popoverContent={
        <div className="column-gap-16px">
          <p>This is the type of email report a user may receive.</p>
          <p>
            <b>Team Leader:</b> This is a report with key patterns, recommended
            actions, and a completion analysis based on{" "}
            {WEEKLY_CHECK_IN_FEATURE_NAME.toLowerCase()}s completed by members
            on a user’s team.
          </p>
          <p>
            <b>Senior Manager:</b> This report is similar to the Team Leader
            report, but based on {WEEKLY_CHECK_IN_FEATURE_NAME.toLowerCase()}s
            completed by members in a user’s reporting structure.
          </p>
        </div>
      }
      trigger="hover"
      popoverTitle="Report Type(s) Received"
      popoverClassName="checkin-score-popover"
    >
      <FontAwesomeIcon
        icon={["far", "circle-info"]}
        style={{ paddingLeft: "5px" }}
      />
    </SimplePopover>
  );

  const getTableRows = () => getFilteredRows().map(getTableRow);

  const getHeaders = () => [
    { label: "User", key: "user" },
    { label: "Date", key: "date" },
    {
      label: "Report Type(s) Received",
      key: "reportType",
      additionalContent: getReportTypePopover(),
    },
  ];

  const rows = getTableRows();
  const headers = getHeaders();

  return (
    <>
      <CheckInEmailReportModal
        show={!!emailReportModalState}
        onHide={() => setEmailReportModalState(null)}
        userAccountId={emailReportModalState?.userAccountId}
        selectReports={emailReportModalState?.reports}
        date={emailReportModalState?.dateSent}
        isSample={!!isSample}
      />
      {getSearchInput("Search by name...", displayedInput, setDisplayedInput)}
      {getDropdownSection()}
      <SortableTable
        rows={rows}
        tableClassName="admin-console-table"
        columnHeaders={[...headers]}
      />
      {getEmptyState()}
    </>
  );
};

export default CheckInActivityEmailReportsTable;
